/**
 * 取り込みの記録の一覧（issue #89・docs/01 6-4「結果はあとから何度でも見返せます」）。
 *
 * 見込み（押す前に出したもの）と、実行（実際に取り込んだもの）の両方が残ります。
 * どちらだったかは「種類」の列でお分かりいただけます。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/context.js';
import type { RosterImportPage } from '../api/types.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { Button } from '../components/ui/Button.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { formatTableDateTime } from '../format/datetime.js';

const PAGE_SIZE = 20;

export function RosterImportsPage(): ReactNode {
  const api = useApi();
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [page, setPage] = useState<RosterImportPage | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const before = cursors[pageIndex];

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setError(null);
    api
      .get<RosterImportPage>('/admin/roster/imports', {
        query: { limit: PAGE_SIZE, ...(before !== undefined ? { before } : {}) },
      })
      .then((loaded) => {
        if (!cancelled) setPage(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, before, reloadKey]);

  function showNext(): void {
    const next = page?.nextCursor;
    if (!next) return;
    setCursors((prev) => [...prev.slice(0, pageIndex + 1), next]);
    setPageIndex((index) => index + 1);
  }

  return (
    <article>
      <PageHeader
        title="取り込みの記録"
        description="これまでの取り込みを新しい順に並べています。行を開くと、そのときの件数とエラー行を何度でも見返せます。"
        actions={
          <Link className="ui-button ui-button-secondary" to="/roster/import">
            CSVでまとめて登録
          </Link>
        }
      />

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !page ? <LoadingBlock /> : null}
      {page && page.items.length === 0 ? (
        <EmptyBlock label="取り込みの記録はまだありません。" />
      ) : null}

      {page && page.items.length > 0 ? (
        <DataTable label="取り込みの記録">
          <thead>
            <tr>
              <th scope="col">日時</th>
              <th scope="col">種類</th>
              <th scope="col">ファイル</th>
              <th scope="col">登録</th>
              <th scope="col">更新</th>
              <th scope="col">取り込めない行</th>
              <th scope="col">
                <span className="admin-visually-hidden">開く</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((item) => (
              <tr key={item.id}>
                <th scope="row">{formatTableDateTime(item.createdAt)}</th>
                <td>{item.dryRun ? '取り込む前の確認' : '取り込み'}</td>
                <td className="ui-cell-wrap">{item.sourceName ?? '（名前なし）'}</td>
                <td className="ui-cell-num">{item.summary?.created ?? 0}件</td>
                <td className="ui-cell-num">{item.summary?.updated ?? 0}件</td>
                <td className="ui-cell-num">{item.summary?.errors ?? 0}件</td>
                <td>
                  <Link
                    className="ui-button ui-button-secondary ui-button-compact"
                    to={`/roster/imports/${item.id}`}
                  >
                    開く
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}

      {/* 役職で断られたときは、ページ送りを出さない（#329-11） */}
      {error?.status === 403 ? null : (
        <nav className="admin-pager" aria-label="ページ送り">
          <Button
            variant="secondary"
            compact
            onClick={() => setPageIndex((index) => Math.max(0, index - 1))}
            disabled={pageIndex === 0}
          >
            前へ
          </Button>
          <span>{pageIndex + 1}ページ目</span>
          <Button variant="secondary" compact onClick={showNext} disabled={!page?.nextCursor}>
            次へ
          </Button>
        </nav>
      )}
    </article>
  );
}
